"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePrivy } from "@privy-io/react-auth";

import PortalNavBar from "../components/PortalNavBar";
import { backendFetch, getCurrentUser } from "../lib/backend";

type RevenueHistoryRecord = {
  id: number;
  revenue_date: string;
  revenue_amount: number;
  distributed: boolean;
};

function formatAmount(value: unknown) {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
}

function formatDate(dateStr: string) {
  if (!dateStr) return "Recorded";
  const date = new Date(dateStr);
  return Number.isNaN(date.getTime())
    ? dateStr
    : date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

export const dynamic = "force-dynamic";

export default function RevenueHistoryPage() {
  const [revenueHistory, setRevenueHistory] = useState<RevenueHistoryRecord[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { ready, authenticated, login, getAccessToken } = usePrivy();

  const navLinks = [
    { href: "/dashboard", label: "Dashboard" },
    { href: "/account", label: "Account" },
    { href: "/accounts-breakdown", label: "Accounts Breakdown" },
    { href: "/agpsira-checkout-app", label: "AGPSIRA Checkout App" },
    { href: "/revenue-history", label: "Revenue History", active: true },
    { href: "/earnings-history", label: "Earnings History" },
    { href: "/sales-history", label: "Sales History" },
    { href: "/distribution-controls", label: "Distribution Controls" },
  ];

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const accessToken = await getAccessToken();

      if (!accessToken) {
        throw new Error("Access token unavailable. Please sign in.");
      }

      const account = await getCurrentUser(accessToken);

      const response = await backendFetch(
        `/api/customer/${account.user.walletAddress}/revenue-history`,
        accessToken
      );

      if (!response.ok) {
        throw new Error(
          `Request failed with status ${response.status}.`
        );
      }

      const payload = await response.json();

      setRevenueHistory(
        Array.isArray(payload?.history) ? payload.history : []
      );
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Unable to load revenue history."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ready || !authenticated) {
      setLoading(false);
      return;
    }

    void loadHistory();
  }, [authenticated, getAccessToken, ready]);

  const totalRevenue = revenueHistory.reduce(
    (sum, record) => sum + Number(record.revenue_amount || 0),
    0
  );

  if (!ready || !authenticated) {
    return (
      <main className="min-h-screen bg-slate-950 px-4 py-12 text-white md:px-8 md:py-16">
        <section className="mx-auto max-w-xl rounded-3xl border border-slate-800 bg-slate-900 p-8 text-center">
          <div className="mb-2 inline-block rounded-full bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300">
            PSE Participant Portal
          </div>
          <h1 className="mt-2 text-3xl font-bold">Revenue History</h1>
          <p className="mt-3 text-slate-300">
            Sign in with your registered email or wallet to review historical revenue events and distribution status.
          </p>
          <button
            type="button"
            onClick={login}
            className="mt-8 w-full rounded-2xl bg-blue-600 py-4 text-base font-semibold text-white hover:bg-blue-500 shadow-lg shadow-blue-600/30"
          >
            Sign In to View Revenue History
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="mb-2 inline-block rounded-full bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300">
              PSE / Client Portal
            </div>
            <h1 className="text-4xl font-bold md:text-6xl">Revenue History</h1>
            <p className="mt-2 text-lg text-slate-300">
              Review historical revenue events and distribution status within your AGPSIRA Agreement.
            </p>
          </div>

          <Link
            href="/dashboard"
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 hover:border-slate-500"
          >
            Back to Dashboard
          </Link>
        </div>

        <PortalNavBar links={navLinks} portalTitle="Participant Navigation" />

        {error && (
          <div className="mb-8 rounded-3xl border border-red-500/30 bg-slate-900 p-8 text-slate-200">
            <h2 className="mb-3 text-2xl font-semibold text-red-300">
              Unable to load revenue history
            </h2>
            <p className="mb-4 text-sm text-slate-300">{error}</p>
            <div className="flex flex-wrap gap-3">
              {(error.includes("401") || error.includes("unverified") || error.includes("token")) ? (
                <button
                  type="button"
                  onClick={login}
                  className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-500"
                >
                  Sign In Again
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void loadHistory()}
                className="rounded-full bg-red-500 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-red-400"
              >
                Retry request
              </button>
            </div>
          </div>
        )}

        {/* Summary Card */}
        <div className="mb-12">
          <div className="max-w-md rounded-3xl border border-slate-800 bg-slate-900 p-8">
            <p className="mb-2 text-slate-400">Lifetime Revenue</p>
            <h2 className="text-4xl font-bold text-blue-400">
              ${formatAmount(totalRevenue)}
            </h2>
          </div>
        </div>

        {/* Revenue Table */}
        <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900">
          <div className="hidden md:grid md:grid-cols-3 bg-slate-800 p-5 font-semibold text-slate-100">
            <div>Date</div>
            <div>Revenue Amount</div>
            <div>Status</div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-slate-400">Loading revenue history...</div>
          ) : revenueHistory.length === 0 ? (
            <div className="p-10 text-center text-slate-400">
              No revenue history records found.
            </div>
          ) : (
            revenueHistory.map((record) => (
              <div
                key={record.id}
                className="grid gap-4 border-t border-slate-800 p-6 md:grid-cols-3 md:p-5"
              >
                <div className="text-slate-300">
                  {formatDate(record.revenue_date)}
                </div>

                <div className="font-semibold text-blue-400">
                  ${formatAmount(record.revenue_amount)}
                </div>

                <div>
                  <span
                    className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
                      record.distributed
                        ? "bg-emerald-500/20 text-emerald-300"
                        : "bg-amber-500/20 text-amber-300"
                    }`}
                  >
                    {record.distributed ? "Distributed" : "Pending Distribution"}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </main>
  );
}